import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2 } from 'lucide-react';
import { WordData } from '../types/editing';
import { BlockData, BlockType, BLOCK_TYPES } from '../config/wordOverlayConfig';
import { useWordDataContext } from '../contexts/WordDataContext';

interface TextractBlockEditorProps {
  block: BlockData;
  imageDimensions: { width: number; height: number };
  onCancel: () => void;
  onHighlightBlock?: (blockId: number, boundingBox: { x: number; y: number; width: number; height: number }) => void;
  onClearHighlight?: () => void;
  allBlocks?: BlockData[]; // Add all blocks for move-to-block functionality
  onBlockChange?: (updatedBlock: BlockData) => void; // New prop to notify of changes
  onBlockDelete?: (blockId: number) => void; // New prop to notify when block should be deleted
  onReadFromWord?: (wordId: number | string) => void; // TTS function to read from a specific word
  pageIndex: number; // Page index for word selection matching
}

const TextractBlockEditor: React.FC<TextractBlockEditorProps> = ({
  block,
  imageDimensions,
  onCancel,
  onHighlightBlock,
  onClearHighlight,
  allBlocks = [],
  onBlockChange,
  onBlockDelete,
  onReadFromWord,
  pageIndex
}) => {
  const { state: wordDataState } = useWordDataContext();
  
  // Debug: Log selectedWord changes
  useEffect(() => {
    console.log('[TextractBlockEditor] selectedWord changed:', wordDataState.selectedWord, 'pageIndex:', pageIndex);
  }, [wordDataState.selectedWord, pageIndex]);
  const [editedBlock, setEditedBlock] = useState<BlockData>(block);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedWordId, setDraggedWordId] = useState<number | null>(null);
  const [dragOverWordId, setDragOverWordId] = useState<number | null>(null);
  const [isDraggingWord, setIsDraggingWord] = useState(false);
  const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number } | null>(null);
  const [originalWordPos, setOriginalWordPos] = useState<{ x: number; y: number } | null>(null);
  const [isReorderingWord, setIsReorderingWord] = useState(false);
  const [reorderDragWordId, setReorderDragWordId] = useState<number | string | null>(null);
  const [reorderDragStartY, setReorderDragStartY] = useState<number | null>(null);
  const [reorderDragOffset, setReorderDragOffset] = useState<number>(0);
  const reorderDragOffsetRef = useRef<number>(0);
  const isInternalUpdate = useRef(false);
  const wordInputRefs = useRef<Map<number | string, HTMLInputElement>>(new Map());
  const wordItemRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());

  useEffect(() => {
    // Only update if this is not an internal update (from onBlockChange)
    if (!isInternalUpdate.current) {
      setEditedBlock(block);
      setHasChanges(false);
    }
    isInternalUpdate.current = false;
  }, [block]);

  // Highlight the block when editor opens (only once)
  useEffect(() => {
    if (onHighlightBlock) {
      const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
      onHighlightBlock(blockIdNum, block.boundingBox);
    }
    
    // Cleanup: clear highlight when component unmounts
    return () => {
      if (onClearHighlight) {
        onClearHighlight();
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Auto-focus and select text in the first empty word when editor opens
  useEffect(() => {
    // Find the first word with empty text
    const firstEmptyWord = editedBlock.words.find(word => !word.text || word.text.trim() === '');
    if (firstEmptyWord) {
      // Use setTimeout to ensure the input is rendered
      setTimeout(() => {
        const input = wordInputRefs.current.get(firstEmptyWord.wordId);
        if (input) {
          input.focus();
          input.select();
        }
      }, 100);
    }
  }, [editedBlock.words.length]); // Run when words are added


  // Block-level editing functions
  const handleBlockTypeChange = (newType: BlockType) => {
    const updatedBlock = {
      ...editedBlock,
      blockType: newType
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleBlockReadOrderChange = (order: number) => {
    const updatedBlock = {
      ...editedBlock,
      blockReadOrder: order
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  // Word-level editing functions
  const handleWordTextChange = (wordId: number, newText: string) => {
    const updatedBlock = {
      ...editedBlock,
      words: editedBlock.words.map(word => 
        word.wordId === wordId ? { ...word, text: newText } : word
      )
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    recalculateBlockDimensions();
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleWordInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentWordId: number | string) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      
      const currentIndex = editedBlock.words.findIndex(w => w.wordId === currentWordId);
      if (currentIndex === -1) return;
      
      let targetIndex: number;
      if (e.shiftKey) {
        // Shift+Tab: go to previous word
        targetIndex = currentIndex - 1;
        if (targetIndex < 0) {
          // If at first word, wrap to last word or stay at first
          targetIndex = editedBlock.words.length > 0 ? editedBlock.words.length - 1 : 0;
        }
      } else {
        // Tab: go to next word
        targetIndex = currentIndex + 1;
        if (targetIndex >= editedBlock.words.length) {
          // If at last word, wrap to first word or stay at last
          targetIndex = 0;
        }
      }
      
      const targetWord = editedBlock.words[targetIndex];
      if (targetWord) {
        const targetInput = wordInputRefs.current.get(targetWord.wordId);
        if (targetInput) {
          targetInput.focus();
          targetInput.select();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      // Close the block editor
      onCancel();
    }
  };

  const handleWordMove = (wordId: number, newPosition: { x: number; y: number }) => {
    const updatedBlock = {
      ...editedBlock,
      words: editedBlock.words.map(word => 
        word.wordId === wordId 
          ? { 
              ...word, 
              boundingBox: {
                ...word.boundingBox,
                x: newPosition.x,
                y: newPosition.y
              } 
            } 
          : word
      )
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    recalculateBlockDimensions();
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleWordResize = (wordId: number, newBounds: { x: number; y: number; width: number; height: number }) => {
    const updatedBlock = {
      ...editedBlock,
      words: editedBlock.words.map(word => 
        word.wordId === wordId 
          ? { 
              ...word, 
              boundingBox: newBounds
            } 
          : word
      )
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    recalculateBlockDimensions();
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleWordDelete = (wordId: number) => {
    // Check if this is the only word in the block - if so, delete the entire block
    if (editedBlock.words.length === 1) {
      const blockIdNum = typeof editedBlock.blockId === 'string' ? parseInt(editedBlock.blockId, 10) : editedBlock.blockId;
      if (onBlockDelete) {
        onBlockDelete(blockIdNum);
      }
      // Close the editor
      onCancel();
      return;
    }
    
    const filteredWords = editedBlock.words.filter(word => word.wordId !== wordId);
    
    // Recalculate block bounding box based on remaining words (same as when moving words)
    let newBlockBounds: { x: number; y: number; width: number; height: number };
    if (filteredWords.length === 0) {
      newBlockBounds = { x: 0, y: 0, width: 0, height: 0 };
    } else {
      const minX = Math.min(...filteredWords.map(w => w.boundingBox.x));
      const minY = Math.min(...filteredWords.map(w => w.boundingBox.y));
      const maxX = Math.max(...filteredWords.map(w => w.boundingBox.x + w.boundingBox.width));
      const maxY = Math.max(...filteredWords.map(w => w.boundingBox.y + w.boundingBox.height));
      newBlockBounds = {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      };
    }
    
    const updatedBlock = {
      ...editedBlock,
      words: filteredWords,
      boundingBox: newBlockBounds
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    recalculateBlockDimensions();
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleWordReorder = (wordId: number, direction: 'up' | 'down') => {
    const currentIndex = editedBlock.words.findIndex(w => w.wordId === wordId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= editedBlock.words.length) return;

    const newWords = [...editedBlock.words];
    [newWords[currentIndex], newWords[newIndex]] = [newWords[newIndex], newWords[currentIndex]];

    const updatedBlock = {
      ...editedBlock,
      words: newWords
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  // Word drag and drop handlers
  const handleWordDragStart = (e: React.DragEvent, wordId: number) => {
    setDraggedWordId(wordId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleWordDragOver = (e: React.DragEvent, wordId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverWordId(wordId);
  };

  const handleWordDragLeave = () => {
    setDragOverWordId(null);
  };

  const handleWordDrop = (e: React.DragEvent, targetWordId: number) => {
    e.preventDefault();
    
    if (!draggedWordId || draggedWordId === targetWordId) {
      setDraggedWordId(null);
      setDragOverWordId(null);
      return;
    }

    // Get current words array
    const currentWords = [...editedBlock.words];
    
    // Find indices of dragged and target words
    const draggedIndex = currentWords.findIndex(word => word.wordId === draggedWordId);
    const targetIndex = currentWords.findIndex(word => word.wordId === targetWordId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedWordId(null);
      setDragOverWordId(null);
      return;
    }

    // Remove dragged word and insert at new position
    const draggedWord = currentWords[draggedIndex];
    const newWords = [...currentWords];
    newWords.splice(draggedIndex, 1);
    newWords.splice(targetIndex, 0, draggedWord);

    const updatedBlock = {
      ...editedBlock,
      words: newWords
    };
    
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    recalculateBlockDimensions();
    
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
    
    setDraggedWordId(null);
    setDragOverWordId(null);
  };

  // Word position dragging handlers
  const handleWordMouseDown = (e: React.MouseEvent, wordId: number) => {
    e.preventDefault();
    const word = editedBlock.words.find(w => w.wordId === wordId);
    if (!word) return;

    setIsDraggingWord(true);
    setDraggedWordId(wordId);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setOriginalWordPos({ x: word.boundingBox.x, y: word.boundingBox.y });
  };

  const handleWordMouseMove = (e: MouseEvent) => {
    if (!isDraggingWord || !draggedWordId || !dragStartPos || !originalWordPos) return;

    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    
    // Scale coordinates to image dimensions
    const scaleX = imageDimensions.width / (e.target as Element)?.getBoundingClientRect().width || 1;
    const scaleY = imageDimensions.height / (e.target as Element)?.getBoundingClientRect().height || 1;
    
    const newX = originalWordPos.x + (deltaX * scaleX);
    const newY = originalWordPos.y + (deltaY * scaleY);

    const updatedBlock = {
      ...editedBlock,
      words: editedBlock.words.map(word => 
        word.wordId === draggedWordId 
          ? { 
              ...word, 
              boundingBox: {
                ...word.boundingBox,
                x: Math.max(0, newX),
                y: Math.max(0, newY)
              }
            } 
          : word
      )
    };
    
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleWordMouseUp = () => {
    if (!isDraggingWord) return;
    
    setIsDraggingWord(false);
    setDraggedWordId(null);
    setDragStartPos(null);
    setOriginalWordPos(null);
    recalculateBlockDimensions();
  };

  // Add global mouse event listeners for word dragging
  useEffect(() => {
    if (isDraggingWord) {
      document.addEventListener('mousemove', handleWordMouseMove);
      document.addEventListener('mouseup', handleWordMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleWordMouseMove);
        document.removeEventListener('mouseup', handleWordMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDraggingWord, draggedWordId, dragStartPos, originalWordPos, imageDimensions]);

  // Word reordering handlers (for drag handle in list)
  const handleReorderMouseDown = (e: React.MouseEvent, wordId: number | string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReorderingWord(true);
    setReorderDragWordId(wordId);
    setReorderDragStartY(e.clientY);
    setReorderDragOffset(0);
    reorderDragOffsetRef.current = 0;
  };

  const handleReorderMouseMove = useCallback((e: MouseEvent) => {
    if (!isReorderingWord || !reorderDragWordId || reorderDragStartY === null) return;

    const currentY = e.clientY;
    const deltaY = currentY - reorderDragStartY;
    setReorderDragOffset(deltaY);
    reorderDragOffsetRef.current = deltaY;
  }, [isReorderingWord, reorderDragWordId, reorderDragStartY]);

  const handleReorderMouseUp = useCallback((e: MouseEvent) => {
    if (!isReorderingWord || !reorderDragWordId || reorderDragStartY === null) {
      setIsReorderingWord(false);
      setReorderDragWordId(null);
      setReorderDragStartY(null);
      setReorderDragOffset(0);
      reorderDragOffsetRef.current = 0;
      return;
    }

    const currentWords = [...editedBlock.words];
    const draggedIndex = currentWords.findIndex(w => w.wordId === reorderDragWordId);
    
    if (draggedIndex === -1) {
      setIsReorderingWord(false);
      setReorderDragWordId(null);
      setReorderDragStartY(null);
      setReorderDragOffset(0);
      reorderDragOffsetRef.current = 0;
      return;
    }

    // Get actual current mouse Y position
    const currentY = e.clientY;
    
    // Find target index based on actual mouse position
    // Get all word items with their positions and indices (excluding the dragged one)
    const wordItems = Array.from(wordItemRefs.current.entries())
      .filter(([id]) => id !== reorderDragWordId)
      .map(([id, element]) => {
        const rect = element.getBoundingClientRect();
        const wordIndex = currentWords.findIndex(w => w.wordId === id);
        return {
          id,
          wordIndex,
          top: rect.top,
          bottom: rect.bottom,
          center: rect.top + rect.height / 2,
          distance: Math.abs(currentY - (rect.top + rect.height / 2)) // Distance from mouse to center
        };
      })
      .sort((a, b) => a.wordIndex - b.wordIndex); // Sort by word index order

    let targetIndex = draggedIndex; // Default to original position
    
    if (wordItems.length === 0) {
      // No other items, can't reorder
      targetIndex = draggedIndex;
    } else {
      // Find the closest item to the mouse
      const closestItem = wordItems.reduce((closest, item) => 
        item.distance < closest.distance ? item : closest
      );
      
      // Check if mouse is directly over an item
      const itemUnderMouse = wordItems.find(item => 
        currentY >= item.top && currentY <= item.bottom
      );
      
      if (itemUnderMouse) {
        // Mouse is directly over an item - insert before or after based on which half
        if (currentY < itemUnderMouse.center) {
          targetIndex = itemUnderMouse.wordIndex; // Insert before
        } else {
          targetIndex = itemUnderMouse.wordIndex + 1; // Insert after
        }
      } else {
        // Mouse is not directly over an item - use closest item
        if (currentY < closestItem.top) {
          // Mouse is above closest item - insert before it
          targetIndex = closestItem.wordIndex;
        } else if (currentY > closestItem.bottom) {
          // Mouse is below closest item - insert after it
          targetIndex = closestItem.wordIndex + 1;
        } else {
          // Shouldn't happen, but fallback to closest item center
          if (currentY < closestItem.center) {
            targetIndex = closestItem.wordIndex;
          } else {
            targetIndex = closestItem.wordIndex + 1;
          }
        }
      }
      
      // Clamp to valid range
      targetIndex = Math.max(0, Math.min(targetIndex, currentWords.length));
    }

    // Adjust target index to account for the item being removed
    // If we're moving down (targetIndex > draggedIndex), we need to subtract 1
    // because removing the item shifts all indices down by 1
    let adjustedTargetIndex = targetIndex;
    if (targetIndex > draggedIndex) {
      adjustedTargetIndex = targetIndex - 1;
    }

    // Reorder the words
    if (adjustedTargetIndex !== draggedIndex && adjustedTargetIndex >= 0 && adjustedTargetIndex <= currentWords.length) {
      const draggedWord = currentWords[draggedIndex];
      const newWords = [...currentWords];
      newWords.splice(draggedIndex, 1);
      newWords.splice(adjustedTargetIndex, 0, draggedWord);

      const updatedBlock = {
        ...editedBlock,
        words: newWords
      };
      
      setEditedBlock(updatedBlock);
      setHasChanges(true);
      recalculateBlockDimensions();
      
      // Notify parent of changes for staging
      if (onBlockChange) {
        isInternalUpdate.current = true;
        onBlockChange(updatedBlock);
      }
    }

    setIsReorderingWord(false);
    setReorderDragWordId(null);
    setReorderDragStartY(null);
    setReorderDragOffset(0);
    reorderDragOffsetRef.current = 0;
  }, [isReorderingWord, reorderDragWordId, reorderDragStartY, editedBlock.words, onBlockChange]);

  // Set up mouse event listeners for reordering
  useEffect(() => {
    if (isReorderingWord) {
      document.addEventListener('mousemove', handleReorderMouseMove);
      document.addEventListener('mouseup', handleReorderMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleReorderMouseMove);
        document.removeEventListener('mouseup', handleReorderMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isReorderingWord, handleReorderMouseMove, handleReorderMouseUp]);

  const handleAddWord = (position: { x: number; y: number }, afterWordId?: number) => {
    // Generate new wordId by finding max and adding 1 (same as WordDataContext)
    const maxWordId = editedBlock.words.length > 0 
      ? Math.max(...editedBlock.words.map(w => typeof w.wordId === 'string' ? parseInt(w.wordId) : w.wordId)) 
      : 0;
    const newWordId = maxWordId + 1;
    
    const newWord: WordData = {
      wordId: newWordId,
      text: '-',
      confidence: 100,
      boundingBox: {
        x: position.x,
        y: position.y,
        width: 60,
        height: 20
      },
      run_id: 0 // Add missing run_id property
    };

    let newWords = [...editedBlock.words];
    if (afterWordId) {
      const afterIndex = newWords.findIndex(w => w.wordId === afterWordId);
      newWords.splice(afterIndex + 1, 0, newWord);
    } else {
      newWords.push(newWord);
    }

    const updatedBlock = {
      ...editedBlock,
      words: newWords
    };
    setEditedBlock(updatedBlock);
    setHasChanges(true);
    recalculateBlockDimensions();
    // Notify parent of changes for staging
    if (onBlockChange) {
      isInternalUpdate.current = true;
      onBlockChange(updatedBlock);
    }
  };

  const handleStartAddWord = (afterWordId?: number) => {
    // Find the word we're adding after
    const afterWord = afterWordId ? editedBlock.words.find(w => w.wordId === afterWordId) : null;
    
    // Calculate position for new word - place it to the right of the word it's being added after
    // or at the end of the block if no specific word
    let newPosition: { x: number; y: number };
    
    if (afterWord) {
      // Place new word to the right of the word we're adding after
      newPosition = {
        x: afterWord.boundingBox.x + afterWord.boundingBox.width + 10, // 10px spacing
        y: afterWord.boundingBox.y
      };
    } else if (editedBlock.words.length > 0) {
      // Place at the end of the word list
      const lastWord = editedBlock.words[editedBlock.words.length - 1];
      newPosition = {
        x: lastWord.boundingBox.x + lastWord.boundingBox.width + 10, // 10px spacing
        y: lastWord.boundingBox.y
      };
    } else {
      // No words yet, place at block's top-left
      newPosition = {
        x: editedBlock.boundingBox.x,
        y: editedBlock.boundingBox.y
      };
    }
    
    // Immediately add the word
    handleAddWord(newPosition, afterWordId);
  };


  const handleCreateNewBlock = (position: { x: number; y: number; width: number; height: number }) => {
    const newWord: WordData = {
      wordId: Date.now(), // Use timestamp as number ID
      text: 'New Block',
      confidence: 100,
      boundingBox: position,
      run_id: 0 // Add missing run_id property
    };

    const newBlock: BlockData = {
      blockId: Date.now(), // Use timestamp as number ID
      blockType: 'content',
      boundingBox: position,
      words: [newWord],
      blockReadOrder: editedBlock.blockReadOrder || 1,
      'ai-confidence': 100,
      possibleIssue: '-',
      // Note: review, modifiedWords, mergedFromBlocks are EnhancedBlockData properties, not BlockData
      // They will be preserved via originalBlockData if present
      readBlock: true
    };

    // This would need to be handled at a higher level since it creates a new block
    setHasChanges(true);
  };

  // Auto-recalculate block dimensions when words change
  const recalculateBlockDimensions = () => {
    if (editedBlock.words.length === 0) return;

    const minX = Math.min(...editedBlock.words.map(w => w.boundingBox.x));
    const minY = Math.min(...editedBlock.words.map(w => w.boundingBox.y));
    const maxX = Math.max(...editedBlock.words.map(w => w.boundingBox.x + w.boundingBox.width));
    const maxY = Math.max(...editedBlock.words.map(w => w.boundingBox.y + w.boundingBox.height));

    setEditedBlock(prev => ({
      ...prev,
      boundingBox: {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      }
    }));
  };


  return (
    <div 
      className="textract-block-editor"
    >
      <div className="editor-header">
        <h3>Edit Block: {editedBlock.blockId}</h3>
        <div className="editor-actions">
          <select
            className="block-type-dropdown"
            value={editedBlock.blockType || ''}
            onChange={(e) => {
              handleBlockTypeChange(e.target.value as BlockType);
            }}
          >
            <option value="">-- No Block Type --</option>
            {BLOCK_TYPES.map(type => (
              <option key={type} value={type}>
                {type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
          <button 
            className="btn-close" 
            onClick={onCancel}
            title="Close Editor"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="editor-content">
        {/* Words Management */}
        <div className="form-section">
          <h4>Words ({editedBlock.words.length})</h4>
          
          <div className="words-list">
            {editedBlock.words.map((word, index) => (
              <div 
                key={word.wordId} 
                className="word-item"
                ref={(el) => {
                  if (el) {
                    wordItemRefs.current.set(word.wordId, el);
                  } else {
                    wordItemRefs.current.delete(word.wordId);
                  }
                }}
                style={{
                  transform: reorderDragWordId === word.wordId && isReorderingWord 
                    ? `translateY(${reorderDragOffset}px)` 
                    : undefined,
                  opacity: reorderDragWordId === word.wordId && isReorderingWord ? 0.5 : 1,
                  zIndex: reorderDragWordId === word.wordId && isReorderingWord ? 1000 : 1,
                }}
              >
                <div
                  className="word-grab-handle"
                  onMouseDown={(e) => handleReorderMouseDown(e, word.wordId)}
                  style={{
                    width: '30px',
                    minWidth: '30px',
                    height: '100%',
                    cursor: 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRight: '1px solid var(--border-primary)',
                    userSelect: 'none',
                  }}
                  title="Drag to reorder word"
                >
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>⋮⋮</span>
                </div>
                <input
                  ref={(el) => {
                    if (el) {
                      wordInputRefs.current.set(word.wordId, el);
                    } else {
                      wordInputRefs.current.delete(word.wordId);
                    }
                  }}
                  type="text"
                  className={(() => {
                    const selectedWordId = wordDataState.selectedWord?.wordId;
                    const selectedPageIndex = wordDataState.selectedWord?.pageIndex;
                    const flashingWordId = wordDataState.flashingWord?.wordId;
                    const flashingPageIndex = wordDataState.flashingWord?.pageIndex;
                    const currentWordIdStr = String(word.wordId);
                    const isSelected = selectedWordId === currentWordIdStr && selectedPageIndex === pageIndex;
                    const isFlashing = flashingWordId === currentWordIdStr && flashingPageIndex === pageIndex;
                    
                    let className = 'inline-text-edit';
                    if (isSelected) {
                      className += ' selected-word-input';
                    } else if (isFlashing) {
                      className += ' flashing-word-input';
                    }
                    return className;
                  })()}
                  value={word.text}
                  onChange={(e) => {
                    const wordIdNum = typeof word.wordId === 'string' ? parseInt(word.wordId, 10) : word.wordId;
                    handleWordTextChange(wordIdNum, e.target.value);
                  }}
                  onKeyDown={(e) => handleWordInputKeyDown(e, word.wordId)}
                  placeholder="Edit word text..."
                  style={{
                    backgroundColor: (() => {
                      const selectedWordId = wordDataState.selectedWord?.wordId;
                      const selectedPageIndex = wordDataState.selectedWord?.pageIndex;
                      const currentWordIdStr = String(word.wordId);
                      const isSelected = selectedWordId === currentWordIdStr && selectedPageIndex === pageIndex;
                      
                      return isSelected ? '#ffeb3b' : undefined;
                    })()
                  }}
                />
                <button 
                  className="btn-add-after"
                  onClick={() => {
                    const wordIdNum = typeof word.wordId === 'string' ? parseInt(word.wordId, 10) : word.wordId;
                    handleStartAddWord(wordIdNum);
                  }}
                  title="Add word after this"
                >
                  +
                </button>
                {onReadFromWord && (
                  <button 
                    className="btn-read-from-word"
                    onClick={() => {
                      const wordIdNum = typeof word.wordId === 'string' ? parseInt(word.wordId, 10) : word.wordId;
                      onReadFromWord(wordIdNum);
                    }}
                    title="Read from this word to end of page"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <Volume2 size={14} />
                  </button>
                )}
                <button 
                  className="btn-delete-word"
                  onClick={() => {
                    const wordIdNum = typeof word.wordId === 'string' ? parseInt(word.wordId, 10) : word.wordId;
                    handleWordDelete(wordIdNum);
                  }}
                  title="Delete word"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextractBlockEditor;
